import { SingleIndex, VoidCallback } from '@deqo/foundation';
import { injectable } from 'inversify';
import { CommandDefinition } from '../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../concepts/commands/CommandDispatcher';
import { KeyExpression } from '../platform/input/KeyExpression';
import { KeyInput } from '../platform/input/KeyInput';

export type ShortcutTarget = VoidCallback | CommandDefinition<unknown> | [CommandDefinition<unknown>, unknown[]];
export type ShortcutInvoker = (d: CommandDispatcher) => void;

export interface ShortcutEntry {
    name: string;
    expression: KeyExpression;
    invoker: ShortcutInvoker;
}

@injectable()
export class ShortcutRegistry {

    private entries = new Map<string, ShortcutEntry>();
    private index = new SingleIndex<number, ShortcutEntry>();

    constructor(private readonly parent?: ShortcutRegistry) {
    }

    public register(name: string, keys: string, target: ShortcutTarget): VoidCallback {
        if (this.entries.has(name)) {
            throw new Error(`ShortcutRegistry: '${name}' already registered`);
        }
        const expression = KeyExpression.parse(keys);
        const invoker = this.createInvoker(target);
        const entry = { name, expression, invoker };
        this.entries.set(name, entry);
        this.index.add(expression.key, entry);
        return () => this.unregister(name);
    }

    public unregister(name: string) {
        return this.entries.delete(name);
    }

    public unregisterPartial(lhs: string) {
        const matches = Array.from(this.entries.keys()).filter(k => k.startsWith(lhs));
        matches.forEach(k => this.entries.delete(k));
    }

    public query(input: KeyExpression | KeyboardEvent | KeyInput) {
        return this.queryLocal(input) ?? this.parent?.query(input) ?? null;
    }

    private createInvoker(target: ShortcutTarget) {
        if (Array.isArray(target) && typeof (target[1]) === 'object' && Array.isArray(target[1])) {
            return (d: CommandDispatcher) => d(target[0], target[1]);
        }
        if (typeof (target) === 'object') {
            return (d: CommandDispatcher) => d(target as any, []);
        }
        if (typeof (target) === 'function') {
            return target;
        }
        throw new Error(`KeyMapping: ${target} is not a valid key dispatcher?`);
    }

    private queryLocal(input: KeyExpression | KeyboardEvent | KeyInput) {
        const inputExpr = KeyExpression.from(input);
        const candExprs = this.index.get(inputExpr.key);
        for (const c of candExprs) {
            if (c.expression.matches(inputExpr)) {
                return c.invoker;
            }
        }
        return null;
    }
}

